import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { PageContent } from '../components/Layout'
import { TrashIcon, CheckCircleIcon, XIcon } from '../components/Icons'

// Email Settings - Gmail and Outlook integration

interface EmailConnection {
  id: number
  provider: 'gmail' | 'outlook' | 'imap'
  email_address: string
  is_active: boolean
  last_sync_at: string | null
  created_at: string
}

interface SyncStatus {
  id: number
  sync_started_at: string
  sync_completed_at: string | null
  status: 'in_progress' | 'completed' | 'failed'
  messages_fetched: number
  messages_new: number
  messages_updated: number
  error_message: string | null
}

const EmailSettings: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [connections, setConnections] = useState<EmailConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState<{ [key: number]: boolean }>({})
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [showImapForm, setShowImapForm] = useState(false)
  const [imapForm, setImapForm] = useState({ email: '', password: '', host: '', port: '993', tls: true })
  const [imapConnecting, setImapConnecting] = useState(false)

  // Use backend API instead of calling EmailService directly (keeps API key secure)
  const API_BASE_URL = '/api/emails'

  useEffect(() => {
    loadConnections()
    
    // Check for OAuth callback params
    const params = new URLSearchParams(window.location.search)
    if (params.get('email_connected')) {
      const provider = params.get('email_connected')
      const email = params.get('email')
      showMessage('success', t('emailSettings.messages.connectionSuccess', { provider, email }))
      
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname)
      loadConnections()
    }
    
    if (params.get('email_error')) {
      const error = params.get('email_error')
      showMessage('error', t('emailSettings.messages.connectionError', { error }))
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const loadConnections = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/connections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to load connections')

      const data = await response.json()
      setConnections(data.connections || [])
    } catch (error) {
      console.error('Error loading connections:', error)
      showMessage('error', t('emailSettings.messages.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const handleConnectGmail = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/oauth/gmail/authorize`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to initiate Gmail OAuth')

      const { authUrl } = await response.json()
      window.location.href = authUrl
    } catch (error) {
      console.error('Error connecting Gmail:', error)
      showMessage('error', t('emailSettings.messages.gmailConnectError'))
    }
  }

  const handleConnectOutlook = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/oauth/outlook/authorize`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to initiate Outlook OAuth')

      const { authUrl } = await response.json()
      window.location.href = authUrl
    } catch (error) {
      console.error('Error connecting Outlook:', error)
      showMessage('error', t('emailSettings.messages.outlookConnectError'))
    }
  }

  const handleDisconnect = async (connectionId: number, provider: string) => {
    if (!confirm(t('emailSettings.messages.disconnectConfirm', { provider }))) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/oauth/disconnect/${connectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to disconnect')

      showMessage('success', t('emailSettings.messages.disconnectSuccess'))
      loadConnections()
    } catch (error) {
      console.error('Error disconnecting:', error)
      showMessage('error', t('emailSettings.messages.disconnectError'))
    }
  }

  const handleConnectImap = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!imapForm.email || !imapForm.password) {
      showMessage('error', 'Please provide email and password')
      return
    }

    try {
      setImapConnecting(true)
      const token = localStorage.getItem('token')
      
      const requestBody: any = {
        email: imapForm.email,
        password: imapForm.password
      }
      
      // Only include custom settings if provided
      if (imapForm.host) {
        requestBody.host = imapForm.host
        requestBody.port = parseInt(imapForm.port)
        requestBody.tls = imapForm.tls
      }
      
      const response = await fetch(`${API_BASE_URL}/oauth/imap/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorData = await response.json()
        
        // Handle specific error types
        if (errorData.error === 'microsoft_oauth_required') {
          showMessage('error', t('emailSettings.errors.microsoftOAuthRequired'))
        } else {
          throw new Error(errorData.message || 'Failed to connect IMAP account')
        }
        return
      }

      showMessage('success', t('emailSettings.messages.connectionSuccess', { provider: 'IMAP', email: imapForm.email }))
      setShowImapForm(false)
      setImapForm({ email: '', password: '', host: '', port: '993', tls: true })
      loadConnections()
    } catch (error: any) {
      console.error('Error connecting IMAP:', error)
      showMessage('error', error.message || t('emailSettings.messages.connectionError', { error: 'Connection failed' }))
    } finally {
      setImapConnecting(false)
    }
  }

  const handleSync = async (connectionId: number) => {
    try {
      setSyncing(prev => ({ ...prev, [connectionId]: true }))
      
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/sync/${connectionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to start sync')

      showMessage('success', t('emailSettings.messages.syncStarted'))
    } catch (error) {
      console.error('Error syncing:', error)
      showMessage('error', t('emailSettings.messages.syncError'))
    } finally {
      setSyncing(prev => ({ ...prev, [connectionId]: false }))
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('emailSettings.neverSynced')
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  const getProviderIcon = (provider: string) => {
    if (provider === 'gmail') {
      return (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
        </svg>
      )
    }
    if (provider === 'imap') {
      return (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    }
    return (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.87 0-.44.11-.86.1-.43.31-.77.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM21.18 13.66h-1.64v1.64h-1.64v-1.64H16.26v-1.64h1.64V10.38h1.64v1.64h1.64M8.34 10.61q0-.58.19-1.08.2-.49.58-.85.39-.35.94-.55.54-.2 1.2-.2.58 0 1.07.18.49.18.86.5l-.68.88q-.3-.24-.58-.36-.28-.12-.61-.12-.31 0-.56.1-.24.1-.41.28-.17.18-.26.43-.08.24-.08.53 0 .29.08.54t.26.44q.17.19.41.29.25.11.56.11.33 0 .61-.13.29-.13.58-.37l.68.88q-.37.32-.86.5-.49.17-1.07.17-.66 0-1.20-.2-.55-.2-.94-.56-.39-.36-.58-.85-.2-.5-.2-1.08zM14.72 15.94V8.98h1.54v6.96zM17.84 8.98h1.59l1.08 1.98 1.08-1.98h1.59v6.96h-1.54v-4.62l-1.13 2.01h-.02l-1.13-2.01v4.62h-1.54V8.98z"/>
      </svg>
    )
  }

  if (loading) {
    return (
      <PageContent>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('emailSettings.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('emailSettings.subtitle')}
          </p>
        </div>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('emailSettings.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('emailSettings.subtitle')}
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Success/Error Messages */}
        {message && (
          <div className={`mb-6 p-4 rounded-md ${
            message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700' 
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700'
          }`}>
            {message.text}
          </div>
        )}

        {/* Connect New Account Section */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            {t('emailSettings.connectNew')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
            {t('emailSettings.connectDescription')}
          </p>
          
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleConnectGmail}
              className="flex items-center space-x-3 px-6 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-red-500 dark:hover:border-red-400 transition-colors"
            >
              <div className="text-red-500">{getProviderIcon('gmail')}</div>
              <span className="font-medium text-gray-900 dark:text-white">
                {t('emailSettings.connectGmail')}
              </span>
            </button>

            <button
              onClick={handleConnectOutlook}
              className="flex items-center space-x-3 px-6 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-900 dark:hover:border-blue-400 transition-colors"
            >
              <div className="text-gray-900">{getProviderIcon('outlook')}</div>
              <span className="font-medium text-gray-900 dark:text-white">
                {t('emailSettings.connectOutlook')}
              </span>
            </button>

            <button
              onClick={() => setShowImapForm(true)}
              className="flex items-center space-x-3 px-6 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-green-500 dark:hover:border-green-400 transition-colors"
            >
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="font-medium text-gray-900 dark:text-white">
                {t('emailSettings.connectImap')}
              </span>
            </button>
          </div>

          {/* IMAP Connection Form */}
          {showImapForm && (
            <div className="mt-6 p-6 border-2 border-green-300 dark:border-green-600 rounded-lg bg-green-50 dark:bg-green-900/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('emailSettings.imapForm.title')}
                </h3>
                <button
                  onClick={() => setShowImapForm(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {t('emailSettings.imapForm.description')}
              </p>

              <form onSubmit={handleConnectImap} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {t('emailSettings.imapForm.emailLabel')} *
                  </label>
                  <input
                    type="email"
                    value={imapForm.email}
                    onChange={(e) => setImapForm({ ...imapForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('emailSettings.imapForm.emailPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {t('emailSettings.imapForm.passwordLabel')} *
                  </label>
                  <input
                    type="password"
                    value={imapForm.password}
                    onChange={(e) => setImapForm({ ...imapForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('emailSettings.imapForm.passwordPlaceholder')}
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('emailSettings.imapForm.gmailAppPasswordHelp')}: <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:underline">Google</a>
                  </p>
                </div>

                <details className="text-sm">
                  <summary className="cursor-pointer text-gray-700 dark:text-gray-300 font-medium">
                    {t('emailSettings.imapForm.advancedSettings')}
                  </summary>
                  <div className="mt-3 space-y-3 pl-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        {t('emailSettings.imapForm.imapServerLabel')}
                      </label>
                      <input
                        type="text"
                        value={imapForm.host}
                        onChange={(e) => setImapForm({ ...imapForm, host: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder={t('emailSettings.imapForm.imapServerPlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        {t('emailSettings.imapForm.portLabel')}
                      </label>
                      <input
                        type="number"
                        value={imapForm.port}
                        onChange={(e) => setImapForm({ ...imapForm, port: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder={t('emailSettings.imapForm.portPlaceholder')}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={imapForm.tls}
                        onChange={(e) => setImapForm({ ...imapForm, tls: e.target.checked })}
                        className="mr-2"
                      />
                      <label className="text-sm text-gray-700 dark:text-gray-300">
                        {t('emailSettings.imapForm.useTls')}
                      </label>
                    </div>
                  </div>
                </details>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={imapConnecting}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {imapConnecting ? t('emailSettings.imapForm.connecting') : t('emailSettings.imapForm.connect')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowImapForm(false)}
                    className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('emailSettings.imapForm.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Connected Accounts Section */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            {t('emailSettings.connectedAccounts')}
          </h2>

          {connections.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>{t('emailSettings.noConnections')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {connections.map((connection) => (
                <div
                  key={connection.id}
                  className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className={
                      connection.provider === 'gmail' ? 'text-red-500' : 
                      connection.provider === 'imap' ? 'text-green-600' :
                      'text-gray-900'
                    }>
                      {getProviderIcon(connection.provider)}
                    </div>
                    
                    <div>
                        <div className="flex items-center space-x-2">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {connection.email_address}
                        </p>
                        {connection.is_active ? (
                          <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        ) : (
                          <XIcon className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('emailSettings.lastSync')}: {formatDate(connection.last_sync_at)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSync(connection.id)}
                      disabled={syncing[connection.id]}
                      className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
                      title={t('emailSettings.syncNow')}
                    >
                      <svg className={`w-5 h-5 ${syncing[connection.id] ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>

                    <button
                      onClick={() => handleDisconnect(connection.id, connection.provider)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      title={t('emailSettings.disconnect')}
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
            {t('emailSettings.info.title')}
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
            <li>• {t('emailSettings.info.point1')}</li>
            <li>• {t('emailSettings.info.point2')}</li>
            <li>• {t('emailSettings.info.point3')}</li>
          </ul>
        </div>
      </div>
    </PageContent>
  )
}

export default EmailSettings

